import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Package, Search, Boxes, Layers } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function InventoryBatches() {
  const [searchParams] = useSearchParams();
  const productFilter = searchParams.get('product_id') || '';
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  // Show ALL batches by default (including zero-qty) — Sep 2026 requirement.
  const [onlyAvailable, setOnlyAvailable] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    loadBatches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlyAvailable, productFilter]);

  function loadBatches() {
    const token = localStorage.getItem('token');
    setLoading(true);
    const params = new URLSearchParams();
    if (onlyAvailable) params.set('only_available', 'true');
    if (productFilter) params.set('product_id', productFilter);
    axios
      .get(API + '/inventory/batches' + (params.toString() ? '?' + params.toString() : ''), { headers: { Authorization: 'Bearer ' + token } })
      .then((res) => setBatches(res.data || []))
      .catch(() => toast.error('Failed to load batches'))
      .finally(() => setLoading(false));
  }

  const filtered = useMemo(() => {
    if (!search.trim()) return batches;
    const needle = search.toLowerCase();
    return batches.filter(
      (b) =>
        (b.product_name || '').toLowerCase().includes(needle) ||
        (b.batch_number || '').toLowerCase().includes(needle) ||
        String(b.mrp || '').includes(needle),
    );
  }, [batches, search]);

  const summary = useMemo(() => {
    const productIds = new Set(batches.map((b) => b.product_id));
    const totalStock = batches.reduce((s, b) => s + (b.current_stock || 0), 0);
    return {
      products: productIds.size,
      batches: batches.length,
      totalStock: Math.round(totalStock * 100) / 100,
    };
  }, [batches]);

  return (
    <div className="space-y-6" data-testid="inventory-batches-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Layers className="w-8 h-8 text-indigo-600" />
            Inventory Batches
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {productFilter && batches.length > 0
              ? `Showing batches for: ${batches[0].product_name}`
              : 'Multi-MRP batch tracking. Every unique MRP per product is its own batch.'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {productFilter && (
            <a
              href="/inventory/batches"
              className="text-xs px-3 py-1.5 rounded-md border border-slate-300 hover:bg-slate-50 text-slate-700"
              data-testid="clear-product-filter"
            >
              Clear filter
            </a>
          )}
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={onlyAvailable}
              onChange={(e) => setOnlyAvailable(e.target.checked)}
              data-testid="only-available-toggle"
            />
            Only show batches with stock
          </label>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-semibold text-slate-500 uppercase flex items-center gap-2">
              <Package className="w-4 h-4" />
              Products with batches
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold" data-testid="summary-products">
              {summary.products}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-semibold text-slate-500 uppercase flex items-center gap-2">
              <Layers className="w-4 h-4" />
              Active batches
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold" data-testid="summary-batches">
              {summary.batches}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-semibold text-slate-500 uppercase flex items-center gap-2">
              <Boxes className="w-4 h-4" />
              Total units on hand
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold" data-testid="summary-stock">
              {summary.totalStock}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="text-lg">All Batches</CardTitle>
            <div className="relative w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search product, batch #, or MRP..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="batches-search"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto border rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-slate-100">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">Batch #</th>
                  <th className="px-3 py-2 text-left font-semibold">Product</th>
                  <th className="px-3 py-2 text-right font-semibold">MRP</th>
                  <th className="px-3 py-2 text-right font-semibold">Inward Excl</th>
                  <th className="px-3 py-2 text-right font-semibold">Inward Incl</th>
                  <th className="px-3 py-2 text-right font-semibold">GST %</th>
                  <th className="px-3 py-2 text-right font-semibold bg-emerald-50">Stock</th>
                  <th className="px-3 py-2 text-center font-semibold">MFG</th>
                  <th className="px-3 py-2 text-center font-semibold">Expiry</th>
                  <th className="px-3 py-2 text-left font-semibold">Last GRN</th>
                  <th className="px-3 py-2 text-center font-semibold">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {loading ? (
                  <tr>
                    <td colSpan={11} className="px-3 py-6 text-center text-slate-400">
                      Loading...
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="px-3 py-10 text-center text-slate-400">
                      No batches. Create a GRN with MRP &gt; 0 to see batches appear here.
                    </td>
                  </tr>
                ) : (
                  filtered.map((b) => (
                    <tr key={b.id} className="hover:bg-slate-50" data-testid={`batch-row-${b.batch_number}`}>
                      <td className="px-3 py-2 font-mono text-indigo-700">{b.batch_number}</td>
                      <td className="px-3 py-2">{b.product_name}</td>
                      <td className="px-3 py-2 text-right font-semibold">₹{(b.mrp || 0).toFixed(2)}</td>
                      <td className="px-3 py-2 text-right">₹{(b.inward_price_excl_gst || 0).toFixed(2)}</td>
                      <td className="px-3 py-2 text-right">₹{(b.inward_price_incl_gst || 0).toFixed(2)}</td>
                      <td className="px-3 py-2 text-right">{(b.gst_percentage || 0).toFixed(1)}%</td>
                      <td className="px-3 py-2 text-right font-bold bg-emerald-50 text-emerald-800">
                        {b.current_stock}
                      </td>
                      <td className="px-3 py-2 text-center text-slate-600">{b.mfg_date || '—'}</td>
                      <td className="px-3 py-2 text-center text-slate-600">{b.exp_date || '—'}</td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-500">{b.last_grn_number || '—'}</td>
                      <td className="px-3 py-2 text-center">
                        <Badge
                          variant={b.status === 'active' ? 'default' : 'secondary'}
                          className={b.status === 'active' ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' : ''}
                        >
                          {b.status}
                        </Badge>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
